import { isLoading } from "apollo/utils";
import clsx from "clsx";
import {
  CONTRADICTION_STATUS,
  QUERY_PROGRESS_NOT_STARTED,
  RECARE_VOICE_FILE_TYPE,
  SOURCE_DOCUMENT_ORIGINS,
} from "core/consts";
import { useExtractDocs } from "core/model/api/endpoints/extractDocs/context";
import { getStaticAsset } from "core/model/config";
import { formatUnixDate } from "core/model/utils/dates";
import {
  Contradictions,
  DocsFormDataMapped,
  DocsFormName,
  FileState,
  Patient,
  QueryProgress,
} from "core/types";
import {
  Banner,
  Button,
  Card,
  CardBody,
  Chip,
  ChipProps,
  Image,
  Select,
  Spinner,
  Tabs,
  TabsProps,
} from "ds/ui";
import { PdfViewer, PdfViewerFile } from "dsl/atoms/PDFViewer";
import { useCreateFileUrl } from "dsl/ecosystems/PatientFiles/useDownloadFile";
import { isFileV2 } from "dsl/organisms/FileTables";
import {
  AlertTriangleIcon,
  ArrowLeftIcon,
  CheckIcon,
  CircleAlertIcon,
  FileTextIcon,
  MicIcon,
  ShieldAlertIcon,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { useLocale, useTranslations } from "translations";
import Translations from "translations/types";
import { SourceSelected } from "../useDocsForm";

export const isContradictingFile = (
  contradictions?: Contradictions,
  fileId?: string,
): boolean => {
  if (
    !contradictions ||
    !fileId ||
    contradictions.status !== CONTRADICTION_STATUS.CONFLICT
  ) {
    return false;
  }
  return contradictions.candidates.some((candidate) =>
    candidate.source_documents.some((doc) => String(doc.file_id) === fileId),
  );
};

const ContentHeaderBanners = ({
  contradictions,
}: {
  contradictions?: Contradictions;
}) => {
  const translations = useTranslations();

  if (contradictions?.status === CONTRADICTION_STATUS.CONFLICT) {
    return (
      <div className="flex w-full flex-col gap-2">
        <Banner
          color="danger"
          icon={CircleAlertIcon}
          message={
            translations.extractDocs.splitView.sourcesTab
              .bannerContradictingInformation
          }
          withClose
        />
      </div>
    );
  }

  return (
    <Banner
      color="primary"
      message={
        translations.extractDocs.splitView.sourcesTab
          .multipleSourcesUsedInformation
      }
      withClose
    />
  );
};

const getHeaderFileName = ({
  selectedFile,
  translations,
}: {
  selectedFile: PdfViewerFile;
  translations: Translations;
}) => {
  if (selectedFile.id === SOURCE_DOCUMENT_ORIGINS.RECARE_DATA) {
    return translations.extractDocs.splitView.sourcesTab.sourcePatientProfile;
  }

  if (selectedFile.isDeleted) {
    return translations.extractDocs.splitView.sourceInformation.deletedFileName;
  }

  return selectedFile.fileName;
};

const getTabTitle = ({
  file,
  index,
  isConflicting,
  isRecareData,
  translations,
}: {
  file: boolean;
  isConflicting: boolean;
  isRecareData: boolean;
  translations: Translations;
  index: number;
}) => {
  const label = isRecareData
    ? translations.extractDocs.splitView.sourcesTab.tabPatientProfile
    : translations.extractDocs.splitView.sourcesTab.tabTitle({
        count: String(index + 1),
      });

  if (!file && !isRecareData) {
    return (
      <div className="flex items-center gap-1">
        <ShieldAlertIcon className="text-warning-dark" size={16} />
        <span>{label}</span>
      </div>
    );
  }
  if (isConflicting) {
    return (
      <div className="flex items-center gap-1.5">
        <AlertTriangleIcon className="text-danger-dark" size={16} />
        <span>{label}</span>
      </div>
    );
  }
  return label;
};

/** String label for the same tab (for Select trigger display). */
const getTabTitleText = ({
  index,
  isRecareData,
  translations,
}: {
  isRecareData: boolean;
  translations: Translations;
  index: number;
}) =>
  isRecareData
    ? translations.extractDocs.splitView.sourcesTab.tabPatientProfile
    : translations.extractDocs.splitView.sourcesTab.tabTitle({
        count: String(index + 1),
      });

const Header = ({
  contradictions,
  items,
  onBack,
  onSelectionChange,
  selectedFile,
}: {
  items: (TabsProps["items"][number] & { titleText?: string })[];
  selectedFile: PdfViewerFile | undefined;
  contradictions?: Contradictions | undefined;
  onBack: () => void;
  onSelectionChange: (fileId: string) => void;
}) => {
  const translations = useTranslations();
  const locale = useLocale();

  if (!items.length || !selectedFile) {
    return null;
  }

  const selectedKey = String(selectedFile.id);
  const selectedContent = items.find((item) => item.key === selectedKey)
    ?.content;

  if (items.length === 1) {
    return (
      <>
        <div className="flex w-full flex-row items-center gap-4">
          <div className="flex gap-4">
            <Button
              aria-label={translations.actions.back}
              color="primary"
              id="back"
              isIconOnly
              startContent={<ArrowLeftIcon />}
              variant="ghost"
              onPress={onBack}
            />
          </div>
          <div className="flex flex-col">
            <h3 className="text-heading-sm font-bold">
              {getHeaderFileName({ selectedFile, translations })}
            </h3>
            {!!selectedFile.uploadedAt && (
              <p className="text-body-md text-gray-600">
                {translations.extractDocs.splitView.sourcesTab.uploadedFileDateDetail(
                  {
                    date: formatUnixDate(selectedFile.uploadedAt, locale),
                  },
                )}
              </p>
            )}
          </div>
        </div>
        <div className="flex w-full flex-1 flex-col">{items[0].content}</div>
      </>
    );
  }

  const selectItems = items.map((item) => ({
    id: item.key,
    label: item.title,
  }));

  const firstKey = items[0]?.key;

  return (
    <div className="flex h-full w-full flex-col gap-4">
      <ContentHeaderBanners contradictions={contradictions} />
      <div className="flex w-full flex-nowrap items-center gap-4">
        <Button
          aria-label={translations.actions.back}
          color="primary"
          id="back"
          isIconOnly
          startContent={<ArrowLeftIcon />}
          variant="ghost"
          onPress={onBack}
        />
        {items.length >= 5 ? (
          <div className="flex flex-1 flex-row items-center gap-3">
            <p className="text-body-md font-semibold whitespace-nowrap">
              {translations.patientForms.sourceSelectLabel}
            </p>
            <div className="w-full max-w-[183px]">
              <Select
                aria-label={translations.patientForms.sourceSelectLabel}
                items={selectItems}
                renderItem={(item) => item.label}
                renderValue={() =>
                  items.find((i) => i.key === selectedKey)?.titleText ??
                  selectedKey ??
                  null
                }
                selectedKeys={[selectedKey ?? firstKey].filter(Boolean)}
                onSelectionChange={(keys) => {
                  const [key] = [...keys];
                  onSelectionChange(String(key));
                }}
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-1 flex-col">
            <Tabs
              aria-label={translations.extractDocs.splitView.sourcesTab.title}
              classNames={{
                panel: "!hidden",
              }}
              items={items}
              selectedKey={selectedKey}
              variant="underlined"
              onSelectionChange={(key) => onSelectionChange(key.toString())}
            />
            <hr className="w-full border-gray-400" />
          </div>
        )}
      </div>
      {selectedContent != null ? (
        <div className="h-full min-h-0 w-full flex-1">{selectedContent}</div>
      ) : null}
    </div>
  );
};

const ExtractionStatusChip = ({ file }: { file: FileState }) => {
  const translations = useTranslations();
  const { utils } = useExtractDocs();

  const extractionStatus: (ChipProps & { label: string }) | null =
    useMemo(() => {
      if (utils.isFileExtractionFailed(file)) {
        return {
          color: "danger",
          id: "extraction-failed-chip",
          label: translations.extractDocs.extractBanner.extractionError,
          startContent: <AlertTriangleIcon size={16} />,
        };
      }
      if (utils.isFileExtractionProcessing(file)) {
        return {
          color: "primary",
          id: "extraction-processing-chip",
          label: translations.extractDocs.filesTabChanges.buttonExtracting,
          startContent: (
            <Spinner
              color="primary"
              size="xs"
              testId="spinner-extraction-processing"
            />
          ),
        };
      }
      if (utils.isFileExtracted(file)) {
        return {
          color: "success",
          id: "extracted-chip",
          label: translations.extractDocs.filesTabChanges.buttonExtracted,
          startContent: <CheckIcon size={16} />,
        };
      }
      return null;
    }, [
      file.id,
      translations.extractDocs.extractBanner.extractionError,
      translations.extractDocs.filesTabChanges.buttonExtracting,
      translations.extractDocs.filesTabChanges.buttonExtracted,
      utils,
    ]);

  if (!extractionStatus) {
    return null;
  }

  return (
    <Chip
      color={extractionStatus.color}
      id={extractionStatus.id}
      size="sm"
      startContent={extractionStatus.startContent}
      variant="flat"
    >
      {extractionStatus.label}
    </Chip>
  );
};

const SourceContent = ({
  externalId,
  files,
  methods,
  resetSelectedSource,
  selectedSource,
}: {
  resetSelectedSource: () => void;
  files: { name: string; file: FileState }[];
  externalId: string;
  selectedSource: SourceSelected<DocsFormName> | undefined;
  methods: UseFormReturn<DocsFormDataMapped<DocsFormName>>;
}) => {
  const translations = useTranslations();
  const locale = useLocale();
  const [createFileUrlProgress, setCreateFileUrlProgress] =
    useState<QueryProgress>(QUERY_PROGRESS_NOT_STARTED);
  const urlCache = useRef<Map<FileState["id"], string>>(new Map());
  const [selectedFile, setSelectedFile] = useState<PdfViewerFile | undefined>();
  const [loadingFileId, setLoadingFileId] = useState<
    FileState["id"] | undefined
  >();
  const { utils } = useExtractDocs();

  const isRecareData = selectedFile?.id === SOURCE_DOCUMENT_ORIGINS.RECARE_DATA;

  const sourceDocuments = selectedSource?.sourceFields.source_documents ?? [];
  const contradictions = selectedSource?.contradictions;

  const contradictionValue = useMemo(() => {
    const candidate = contradictions?.candidates?.find((candidate) =>
      candidate.source_documents.some(
        (doc) => String(doc.file_id) === SOURCE_DOCUMENT_ORIGINS.RECARE_DATA,
      ),
    );
    return candidate?.value;
  }, [contradictions]);

  const value =
    contradictionValue ??
    (selectedSource?.fieldName != null
      ? methods.getValues(selectedSource.fieldName)
      : undefined);

  const displayedDocuments = useMemo(() => {
    if (
      contradictions?.status === CONTRADICTION_STATUS.CONFLICT &&
      contradictions.candidates?.length
    ) {
      const contradictionDocs = contradictions.candidates.flatMap(
        (c) => c.source_documents ?? [],
      );
      const seen = new Set<string>();
      return contradictionDocs
        .filter((doc) => {
          const key = String(doc.file_id);
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .map((doc) => ({ file_id: doc.file_id }));
    }
    return sourceDocuments ?? [];
  }, [contradictions, sourceDocuments]);

  const createFileUrl = useCreateFileUrl({
    externalId,
    onComplete: () => setLoadingFileId(undefined),
    setProgress: setCreateFileUrlProgress,
  });

  const handleSelectionChange = async ({
    fileId,
    files,
  }: {
    fileId: string;
    files: { name: string; file: FileState }[];
  }) => {
    if (fileId === SOURCE_DOCUMENT_ORIGINS.RECARE_DATA) {
      setSelectedFile({
        fileName: "",
        id: fileId,
        isDeleted: false,
        uploadedAt: undefined,
        url: "",
      });
      setLoadingFileId(undefined);
      return;
    }

    const file = files.find((file) => String(file.file.id) === fileId);

    if (!file) {
      setSelectedFile({
        fileName: "",
        id: fileId,
        isDeleted: true,
        uploadedAt: undefined,
        url: "",
      });
      setLoadingFileId(undefined);
      return;
    }

    const cachedUrl = urlCache.current.get(fileId);
    if (cachedUrl) {
      setSelectedFile({
        fileName: file.name,
        id: fileId,
        uploadedAt: file.file.updated_at!,
        url: cachedUrl,
      });
      setLoadingFileId(undefined);
      return;
    }

    const extractedDocument = utils.getExtractedDocument(file.file);
    if (extractedDocument?.source_document_uri) {
      setSelectedFile({
        fileName: file.name,
        id: fileId,
        uploadedAt: file.file.updated_at!,
        url: extractedDocument.source_document_uri,
      });
      setLoadingFileId(undefined);
      return;
    }

    const url = await createFileUrl(file.file);
    if (url) {
      urlCache.current.set(fileId, url);
      setSelectedFile({
        fileName: file.name,
        id: fileId,
        uploadedAt: file.file.updated_at!,
        url,
      });
    }
  };

  useEffect(() => {
    if (displayedDocuments?.length) {
      handleSelectionChange({
        fileId: String(displayedDocuments[0].file_id),
        files,
      });
    }
  }, [displayedDocuments]);

  if (selectedFile) {
    const panelContent = (
      <div className="h-full w-full flex-1">
        {isRecareData && value ? (
          <div className="flex h-full w-full flex-col gap-4">
            <div className="rounded-lg border border-gray-400 p-4">
              <p className="text-body-md font-bold">{value}</p>
              <p className="text-body-md">
                {
                  translations.extractDocs.splitView.sourcesTab
                    .sourcePatientProfile
                }
              </p>
            </div>
          </div>
        ) : selectedFile?.isDeleted ? (
          <div className="flex h-full w-full flex-1 flex-col">
            <Banner
              color="warning"
              icon={ShieldAlertIcon}
              message={
                translations.extractDocs.splitView.sourceInformation
                  .sourceDeletedTooltip
              }
            />
            <div className="flex w-full flex-1 flex-col items-center justify-center bg-gray-50">
              <Image
                aria-hidden="true"
                className="h-[160px] w-auto overflow-visible"
                src={getStaticAsset("docs-deleted-source.svg")}
              />
              <p className="w-[50%] text-center text-heading-md text-gray-600">
                {
                  translations.extractDocs.splitView.sourceInformation
                    .sourceDeletedDescription
                }
              </p>
            </div>
          </div>
        ) : (
          <PdfViewer selectedFile={selectedFile} />
        )}
      </div>
    );

    const documentsNavigation = (() => {
      const base = displayedDocuments.length
        ? displayedDocuments
        : [{ file_id: selectedFile.id }];
      const hasSelected = base.some(
        (doc) => String(doc.file_id) === String(selectedFile.id),
      );
      if (hasSelected) return base;
      return [{ file_id: selectedFile.id }, ...base];
    })();

    const sourceItems: TabsProps["items"] = documentsNavigation
      .map((sourceDocument, index) => {
        const fileId = String(sourceDocument.file_id);
        const file = files.find((f) => f.file.id == sourceDocument.file_id);
        const isSelected = selectedFile.id === fileId;
        const isRecareData = fileId === SOURCE_DOCUMENT_ORIGINS.RECARE_DATA;
        const isConflicting = isContradictingFile(contradictions, fileId);

        return {
          content: isSelected ? panelContent : null,
          fileId,
          key: fileId,
          title: getTabTitle({
            file: !!file,
            index,
            isConflicting,
            isRecareData,
            translations,
          }),
          titleText: getTabTitleText({
            index,
            isRecareData,
            translations,
          }),
        };
      })
      .sort(
        (a, b) =>
          Number(a.fileId === SOURCE_DOCUMENT_ORIGINS.RECARE_DATA) -
          Number(b.fileId === SOURCE_DOCUMENT_ORIGINS.RECARE_DATA),
      );

    return (
      <div className="flex h-full w-full flex-col items-center gap-4 overflow-y-auto">
        <Header
          items={sourceItems}
          selectedFile={selectedFile}
          onBack={() => {
            setSelectedFile(undefined);
            resetSelectedSource();
          }}
          onSelectionChange={(fileId: string) =>
            handleSelectionChange({
              fileId,
              files,
            })
          }
        />
      </div>
    );
  }

  if (!files?.length) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-4">
        <Image
          alt={translations.extractDocs.splitView.sourcesTab.altTextEmptyState}
          className="h-[160px] w-auto overflow-visible"
          src={getStaticAsset("docs-empty-state.svg")}
        />
        <p className="w-[50%] text-center text-heading-md text-gray-600">
          {translations.extractDocs.splitView.sourcesTab.emptyStateInformation}
        </p>
      </div>
    );
  }

  return (
    <ul className="flex h-fit w-full flex-col gap-4">
      {files.map(({ file, name }) => {
        const isFileLoading =
          isLoading(createFileUrlProgress) && loadingFileId === file.id;
        const isVoiceFile =
          isFileV2(file) &&
          file.recare_file_type === RECARE_VOICE_FILE_TYPE.SUMMARY;

        return (
          <li key={`${name}-${file.updated_at}-${file.id}`} className="w-full">
            <Card
              aria-label={translations.extractDocs.splitView.sourcesTab.ariaLabelFileCard(
                { fileName: name },
              )}
              as="button"
              className="flex w-full flex-row rounded-lg border border-gray-300 bg-white"
              id={`source-card-${file.id}`}
              isLoading={isFileLoading}
              shadow="none"
              trackEventProps={{
                file_id: String(file.id),
                id: "source-card",
              }}
              onPress={() => {
                setLoadingFileId(file.id);
                handleSelectionChange({
                  fileId: String(file.id),
                  files,
                });
              }}
            >
              <CardBody className="flex w-full flex-row items-center justify-start gap-4 px-6 py-4">
                {isFileLoading ? (
                  <div className="flex items-center justify-center">
                    <Spinner
                      color="primary"
                      size="sm"
                      testId="spinner-loading-sources"
                    />
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    {isVoiceFile ? (
                      <MicIcon className="h-6 w-6 text-primary" />
                    ) : (
                      <FileTextIcon className="h-6 w-6 text-primary" />
                    )}
                  </div>
                )}
                <div className="flex flex-1 flex-col gap-1 text-left">
                  <span className="text-body-md font-bold break-all text-gray-900">
                    {name}
                  </span>
                  <div className="flex flex-row items-center gap-1 text-body-sm text-gray-500">
                    <span>{formatUnixDate(file.updated_at!, locale)}</span>
                    {isVoiceFile && (
                      <>
                        <span>•</span>
                        <span>
                          {translations.patientForms.menu.recareVoiceLabel}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center">
                  <ExtractionStatusChip file={file} />
                </div>
              </CardBody>
            </Card>
          </li>
        );
      })}
    </ul>
  );
};

export const DocsModalSources = ({
  files,
  methods,
  patient,
  resetSelectedSource,
  selectedSource,
}: {
  selectedSource: SourceSelected<DocsFormName> | undefined;
  methods: UseFormReturn<DocsFormDataMapped<DocsFormName>>;
  patient: Patient;
  resetSelectedSource: () => void;
  files: { name: string; file: FileState }[];
}) => {
  const translations = useTranslations();

  return (
    <div className="flex h-full w-[50%] flex-col border-l border-gray-400">
      <div className="sticky top-0 w-full p-4">
        <h2 className="text-heading-lg font-bold">
          {translations.extractDocs.splitView.sourcesTab.title}
        </h2>
      </div>
      <hr className="w-full border-gray-400" />
      <div
        className={clsx("flex h-full w-full overflow-y-auto p-4", {
          "bg-gray-50": !files.length,
        })}
      >
        <SourceContent
          externalId={patient.external_id!}
          files={files}
          methods={methods}
          resetSelectedSource={resetSelectedSource}
          selectedSource={selectedSource}
        />
      </div>
    </div>
  );
};
